import React from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Stack, Typography, Skeleton, Select, MenuItem, Pagination, Tooltip, Chip, IconButton, Box } from '@mui/material';
import { MyPaper } from '../../myPaper';
import { IBoardDetails, IItem } from '../types';
import { massageData, PUBLISHABLE } from '../utils';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { setItemsByBoard } from '../../../redux/slices/board';
import { hexToGrayscale } from '../../../utils';
import { CropSquare, Edit, MoreHoriz } from '@mui/icons-material';
import { StyledMenu } from './styledMenu';
import { useCreateTag } from '../../../hooks';
import { SelectBoxOption } from '../../../interfaces';
import { UserAvatar } from '../../userAvatar';

export const Board: React.FC<IBoardDetails> = ({
    fetchBoardItems,
    board,
    entityType,
    getTitle,
    searchDetails,
    refetchBoards,
    filters,
    actions = () => [],
    onClick
}) => {
    const dispatch = useAppDispatch();

    const boardDetails = useAppSelector(store => store.board);
    const items: IItem[] = boardDetails?.itemsByBoard?.[board?.id] ?? [];
    const hasMoved = boardDetails?.hasMoved || false;

    const search = searchDetails?.search ?? '';

    const [isLoading, setIsLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(100);
    const [totalCount, setTotalCount] = React.useState(0);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const updateTag = useCreateTag();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleTagEdit = () => {
        updateTag(refetchBoards, board as unknown as SelectBoxOption, true);
        handleClose();
    };

    const fetchItems = async (page: number, rowsPerPage: number, search: string) => {
        setIsLoading(true);
        await fetchBoardItems(board, page, rowsPerPage, search, filters)
            .then((res) => {
                if (Object.keys(res).includes("error")) {
                    throw res.error.data;
                }
                dispatch(setItemsByBoard({ key: board?.id, value: massageData(entityType, res?.data ?? []) }))
                setTotalCount(res?.totalCount ?? 1);
            })
            .catch((error) => {
                const errorMessage: string = error?.title ?? "Oops! Something went wrong, Unable to get the data"
                enqueueSnackbar(errorMessage, { variant: "error" });
            }).finally(() => {
                setIsLoading(false);
            });
    };

    React.useEffect(() => {
        fetchItems(page, rowsPerPage, search);
        // eslint-disable-next-line
    }, [page, rowsPerPage, filters?.tags]);

    React.useEffect(() => {
        if (hasMoved) {
            fetchItems(page, rowsPerPage, search);
        }
        // eslint-disable-next-line
    }, [hasMoved]);


    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setPage(1);
            fetchItems(1, rowsPerPage, search);
        }, 500)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    return (
        <MyPaper key={board.id} sx={{ p: 2, minWidth: 400, maxWidth: 400, height: "100%" }}>
            <Stack spacing={2} height="100%" width="100%">
                <Stack>
                    <Stack alignItems={'center'} justifyContent={"space-between"} direction={"row"}>
                        <Stack alignItems={'center'} direction={"row"} gap={0.5}>
                            <CropSquare fontSize='small' htmlColor={board.tag_color ?? "secondary"} />
                            <Tooltip title={board.title}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontFamily: 'htrts_medium',
                                        display: "-webkit-box",
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {board.title}
                                </Typography>
                            </Tooltip>
                            <Typography sx={{ bgcolor: (theme) => theme.palette.grey[200], px: 1, borderRadius: 10 }} variant="caption" color="textSecondary">{totalCount}</Typography>
                        </Stack>
                        <Tooltip title={`Actions for column: ${board.title}`} arrow>
                            <IconButton onClick={handleClick}>
                                <MoreHoriz />
                            </IconButton>
                        </Tooltip>
                        <StyledMenu
                            id="column-menu"
                            MenuListProps={{
                                'aria-labelledby': 'column-button',
                            }}
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                        >
                            <MenuItem onClick={handleTagEdit}>
                                <Edit color="primary" />
                                Edit
                            </MenuItem>
                        </StyledMenu>
                    </Stack>
                    <Tooltip title={board.description}>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {board.description}
                        </Typography>
                    </Tooltip>
                </Stack>

                <Droppable droppableId={board.id}>
                    {(provided: any, _: any) => (
                        <Stack
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            overflow="auto"
                            flexGrow={1}
                            spacing={2}
                            height="100%"
                            width="100%"
                            sx={{ paddingRight: "4px" }}
                        >
                            {isLoading ? (
                                [1, 2, 3, 4, 5].map(key => (
                                    <Skeleton key={key} variant="rounded" width="100%" height="46px" />
                                ))
                            ) : (
                                items.map((item, index) => {
                                    const isDragDisabled = PUBLISHABLE[entityType] && item?.released ? true : false;
                                    return (
                                        <Tooltip title={isDragDisabled ? "This entity is published, you can't move it" : ""} key={item._id}>
                                            <span>
                                                <Draggable
                                                    key={item._id}
                                                    draggableId={item._id}
                                                    index={index}
                                                    isDragDisabled={isDragDisabled}
                                                >
                                                    {(provided: any, snapshot: any) => (
                                                        <Stack
                                                            ref={provided.innerRef}
                                                            {...(isDragDisabled ? {} : provided.draggableProps)}
                                                            {...(isDragDisabled ? {} : provided.dragHandleProps)}
                                                            sx={{
                                                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                                                p: 2,
                                                                backgroundColor: (theme) => theme.palette.background.paper,
                                                                '&:hover': {
                                                                    boxShadow: `rgba(0, 0, 0, 0.1) 0px 4px 12px`,
                                                                    backgroundColor: (theme) => theme.palette.primary.light
                                                                },
                                                                ...(snapshot?.isDragging ? {
                                                                    backgroundColor: (theme) => theme.palette.primary.light
                                                                } : {}),
                                                                boxShadow: `rgba(99, 99, 99, 0.2) 0px 2px 8px 0px`,
                                                                borderRadius: 2,
                                                            }}
                                                            onClick={() => onClick(board, item)}
                                                        >
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                gap={2}
                                                            >
                                                                <Box sx={{ cursor: 'default' }}>
                                                                    {getTitle(item)}
                                                                </Box>

                                                                <Box sx={{ cursor: 'pointer' }}>
                                                                    <Tooltip title={item.user_assignment ? `${item.user_assignment.assigned_user_name}` : "Yet to assign user"} arrow placement='top'>
                                                                        <span>
                                                                            <UserAvatar userId={`${item?.user_assignment?.assigned_user_id ?? ""}`} name={`${item?.user_assignment?.assigned_user_name ?? ""}`} avatarUrl='' sx={{ width: 24, height: 24, fontSize: '10px', background: (theme) => item.user_assignment ? undefined : theme.palette.divider }} />
                                                                        </span>
                                                                    </Tooltip>
                                                                </Box>

                                                            </Stack>

                                                            <Typography
                                                                mt={1}
                                                                gutterBottom
                                                                variant="body2"
                                                                sx={{
                                                                    '&:hover': {
                                                                        color: (theme) => theme.palette.primary.main,
                                                                        textDecoration: "underline"
                                                                    },
                                                                    cursor: "pointer"
                                                                }}
                                                            >
                                                                {item.description}
                                                            </Typography>

                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                gap={0.5}
                                                                flexWrap="wrap"
                                                                sx={{ mt: 1 }}
                                                            >
                                                                {item.tags_info?.map((tag: any) => (
                                                                    <Chip variant="outlined" sx={{
                                                                        fontSize: '0.65rem',
                                                                        borderColor: tag?.tag_color,
                                                                        backgroundColor: tag?.tag_color,
                                                                        height: `20px`,
                                                                        cursor: "default",
                                                                        color: (theme) => `${hexToGrayscale(tag?.tag_color) > 150 ? theme.palette.text.secondary : "#FFFFFF"}`
                                                                    }}
                                                                        size={"small"}
                                                                        label={tag?.tag_name ?? ""}
                                                                    />
                                                                ))}
                                                            </Stack>

                                                            <Box sx={{ mt: 1, cursor: 'default' }}>
                                                                {actions(item, board)}
                                                            </Box>
                                                        </Stack>
                                                    )}
                                                </Draggable>
                                            </span>
                                        </Tooltip>
                                    )
                                })
                            )}
                            {provided.placeholder}
                        </Stack>
                    )}
                </Droppable>

                <Stack
                    spacing={1}
                    direction="row"
                    sx={{
                        mt: 2,
                        p: 2,
                        justifyContent: "space-between",
                        width: "100%",
                        backgroundColor: (theme) => theme.palette.background.paper,
                        boxShadow: `rgba(0, 0, 0, 0.06) 0px 2px 4px 0px inset`
                    }}
                >
                    <Select
                        label="Page Size"
                        size="small"
                        variant="standard"
                        value={rowsPerPage}
                        onChange={(e) => {
                            setRowsPerPage(parseInt(`${e.target.value}`));
                            setPage(1);
                        }}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={150}>150</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                    </Select>
                    <Pagination
                        size="small"
                        siblingCount={0}
                        boundaryCount={1}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                        count={Math.ceil((totalCount ?? 0) / rowsPerPage)}
                        page={page}
                        onChange={handleChange}
                    />
                </Stack>
            </Stack>
        </MyPaper>
    );
};